import { API_URL } from '@/constants/env'

import {
  buildPaginatedEndpoint,
  convertDatesInObject,
  fetchJsonWithInit,
} from '@/helpers/api'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type {
  CatalogRecipe,
  MonitorCreate,
  MonitorFeedBatchResult,
  MonitorFeedEntryDetailOut,
  MonitorFeedEntryLineageOut,
  MonitorFeedEntryOut,
  MonitorFeedEntryStatus,
  MonitorFeedSort,
  MonitorFeedStatusCounts,
  MonitorFeedTypeCount,
  MonitorOut,
  MonitorOverviewOut,
  MonitorTargetHit,
  MonitorTargetSearchKind,
  MonitorTargetSearchList,
  MonitorVendorTargetHit,
  MonitorUpdate,
} from '@/types/Monitor'
import type { SlackChannel } from '@/types/slack'

export const MONITOR_FEED_PAGE_SIZE = 50

export interface MonitorRecipeList {
  count: number
  data: CatalogRecipe[]
}

/** Live monitor list shape: `{count, data}` — no `has_more`, no pagination. */
export interface MonitorListResponse {
  count: number
  data: MonitorOverviewOut[]
}

export type MonitorFeedListResponse = PaginatedResponse<MonitorFeedEntryOut> & {
  status_counts: MonitorFeedStatusCounts
  // Per-kind facet counts for the active view (kind filter excluded). Drives the
  // rail's "Alert type" list with pagination-independent counts.
  type_counts: MonitorFeedTypeCount[]
  // The exact all-kinds total for the active view (same scope as `type_counts`,
  // kind filter excluded) — NOT a sum of `type_counts`, since the per-kind facets
  // aren't exhaustive. Use this for the rail's "All types" tally so it stays
  // correct even while a specific kind filter is selected and narrows `count`.
  type_counts_total: number
}

/**
 * Org-wide feed query surface. `sort` is always sent; every other field is an
 * optional facet that omits its query param when undefined. `bookmarked` and
 * `status` are independent (Gmail model: a starred row can also be archived).
 */
export interface OrgFeedQuery {
  resolution?: 'current' | 'notice'
  status?: MonitorFeedEntryStatus
  monitorId?: UUID
  bookmarked?: boolean
  resourceKind?: string
  q?: string
  sort: MonitorFeedSort
}

export interface MonitorSlackChannelCreate {
  installation_id: UUID
  channel_id: string
  channel_name?: string
}

export const EMPTY_FEED_STATUS_COUNTS: MonitorFeedStatusCounts = {
  inbox: 0,
  archived: 0,
  bookmarked: 0,
  all: 0,
  unread: 0,
}

export const EMPTY_FEED_TYPE_COUNTS: MonitorFeedTypeCount[] = []

const MONITOR_DATE_FIELDS = ['created_at']

const FEED_ENTRY_DATE_FIELDS = ['event_at', 'due_at', 'posted_at']

const FEED_LINEAGE_DATE_FIELDS = ['occurred_at']

// All fetchers ride `fetchJsonWithInit`, which throws ApiError carrying the
// response `detail` so callers can surface backend messages (e.g. create's 422
// "Monitor target was not found upstream") inline.

const JSON_HEADERS = { 'Content-Type': 'application/json' }

const monitorsBase = (orgId: UUID): string =>
  `${API_URL}/api/v1/organizations/${orgId}/monitors`

// ─── Live endpoints ──────────────────────────────────────────────────────────
// Recipes, monitor CRUD, monitor Slack channels, and the feed routes (list/
// detail/status/read/bookmark/pursuit) are all served by the backend on this
// branch. List/get return MonitorOverviewOut (overview stats are LIVE);
// create/patch/delete return plain MonitorOut.

export const fetchMonitorRecipes = async (
  orgId: UUID | '',
  accessToken: string | null | undefined,
): Promise<MonitorRecipeList> => {
  if (!orgId) {
    return { count: 0, data: [] }
  }
  if (!accessToken) {
    throw new Error('No access token')
  }
  return fetchJsonWithInit<MonitorRecipeList>(
    `${monitorsBase(orgId)}/recipes`,
    accessToken,
  )
}

// List an organization's monitors. Rows are live MonitorOverviewOut (overview
// stats included). The backend returns the full set and ignores pagination
// params.
export const fetchOrganizationMonitors = async (
  orgId: UUID | '',
  accessToken: string | null | undefined,
): Promise<MonitorListResponse> => {
  if (!orgId) {
    return { count: 0, data: [] }
  }
  if (!accessToken) {
    throw new Error('No access token')
  }
  return convertDatesInObject(
    await fetchJsonWithInit<MonitorListResponse>(
      monitorsBase(orgId),
      accessToken,
    ),
    MONITOR_DATE_FIELDS,
  ) as MonitorListResponse
}

// Fetch a single monitor with live overview stats.
export const fetchMonitor = async (
  monitorId: UUID | '',
  orgId: UUID | '',
  accessToken: string | null | undefined,
): Promise<MonitorOverviewOut | null> => {
  if (!monitorId || !orgId) return null
  if (!accessToken) {
    throw new Error('No access token')
  }
  return convertDatesInObject(
    await fetchJsonWithInit<MonitorOverviewOut>(
      `${monitorsBase(orgId)}/${monitorId}`,
      accessToken,
    ),
    MONITOR_DATE_FIELDS,
  ) as MonitorOverviewOut
}

export const createMonitor = async (
  orgId: UUID | '',
  accessToken: string | null | undefined,
  req: MonitorCreate,
): Promise<MonitorOut> => {
  if (!orgId) {
    throw new Error('No organization id')
  }
  if (!accessToken) {
    throw new Error('No access token')
  }
  return convertDatesInObject(
    await fetchJsonWithInit<MonitorOut>(monitorsBase(orgId), accessToken, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(req),
    }),
    MONITOR_DATE_FIELDS,
  ) as MonitorOut
}

// Patch a monitor (name/params/routing/status). Returns the live MonitorOut —
// merge it over cached overview objects rather than replacing them.
export const updateMonitor = async (
  monitorId: UUID,
  orgId: UUID | '',
  accessToken: string | null | undefined,
  req: MonitorUpdate,
): Promise<MonitorOut> => {
  if (!orgId) {
    throw new Error('No organization id')
  }
  if (!accessToken) {
    throw new Error('No access token')
  }
  return convertDatesInObject(
    await fetchJsonWithInit<MonitorOut>(
      `${monitorsBase(orgId)}/${monitorId}`,
      accessToken,
      {
        method: 'PATCH',
        headers: JSON_HEADERS,
        body: JSON.stringify(req),
      },
    ),
    MONITOR_DATE_FIELDS,
  ) as MonitorOut
}

// Delete a monitor. The backend returns a JSON `null` body, so the shared
// helper's response.json() is safe here.
export const deleteMonitor = async (
  monitorId: UUID,
  orgId: UUID | '',
  accessToken: string | null | undefined,
): Promise<void> => {
  if (!orgId) {
    throw new Error('No organization id')
  }
  if (!accessToken) {
    throw new Error('No access token')
  }
  await fetchJsonWithInit<unknown>(
    `${monitorsBase(orgId)}/${monitorId}`,
    accessToken,
    { method: 'DELETE' },
  )
}

export const fetchMonitorSlackChannels = async (
  orgId: UUID | '',
  accessToken: string | null | undefined,
): Promise<SlackChannel[]> => {
  if (!orgId) {
    return []
  }
  if (!accessToken) {
    throw new Error('No access token')
  }
  const body = await fetchJsonWithInit<{ data: SlackChannel[] }>(
    `${monitorsBase(orgId)}/slack/channels`,
    accessToken,
  )
  return body.data
}

export const createMonitorSlackChannel = async (
  orgId: UUID | '',
  accessToken: string | null | undefined,
  req: MonitorSlackChannelCreate,
): Promise<SlackChannel> => {
  if (!orgId) {
    throw new Error('No organization id')
  }
  if (!accessToken) {
    throw new Error('No access token')
  }
  return fetchJsonWithInit<SlackChannel>(
    `${monitorsBase(orgId)}/slack/channels`,
    accessToken,
    {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(req),
    },
  )
}

export const deleteMonitorSlackChannel = async (
  orgId: UUID | '',
  accessToken: string | null | undefined,
  channelId: UUID,
): Promise<void> => {
  if (!orgId) {
    throw new Error('No organization id')
  }
  if (!accessToken) {
    throw new Error('No access token')
  }
  await fetchJsonWithInit<unknown>(
    `${monitorsBase(orgId)}/slack/channels/${channelId}`,
    accessToken,
    { method: 'DELETE' },
  )
}

// ─── Feed endpoints (live) ───────────────────────────────────────────────────
// Org-scoped over MonitorFeedEntry (status inbox|archived|trashed) with
// per-user read receipts and bookmarks. Feeds stay empty until the webhook
// ingest/fanout pipeline lands — nothing writes feed entries yet.

// List the org-wide feed across every monitor in one folder, scoped/filtered by
// the OrgFeedQuery facets, with dataset-wide counts. The route has NO monitor_id
// path segment; `monitor_id` is an optional QUERY param to scope to one monitor.
// Each returned entry carries `monitor_name` (null only on the per-monitor feed).
export const fetchOrganizationFeed = async (
  orgId: UUID | '',
  accessToken: string | null | undefined,
  page: PaginateRequest,
  query: OrgFeedQuery,
  signal?: AbortSignal,
): Promise<MonitorFeedListResponse> => {
  if (!orgId) {
    return {
      count: 0,
      has_more: false,
      status_counts: EMPTY_FEED_STATUS_COUNTS,
      type_counts: EMPTY_FEED_TYPE_COUNTS,
      type_counts_total: 0,
      data: [],
    }
  }
  if (!accessToken) {
    throw new Error('No access token')
  }
  const params = new URLSearchParams()
  if (query.status) params.set('status', query.status)
  if (query.monitorId) params.set('monitor_id', query.monitorId)
  if (query.bookmarked) params.set('bookmarked', 'true')
  if (query.resourceKind) params.set('resource_kind', query.resourceKind)
  if (query.q) params.set('q', query.q)
  params.set('sort', query.sort)
  if (query.resolution) params.set('resolution', query.resolution)
  const baseUrl = `${monitorsBase(orgId)}/feed?${params.toString()}`
  return convertDatesInObject(
    await fetchJsonWithInit<MonitorFeedListResponse>(
      buildPaginatedEndpoint(baseUrl, page),
      accessToken,
      { signal },
    ),
    FEED_ENTRY_DATE_FIELDS,
  ) as MonitorFeedListResponse
}

// Fetch one feed entry with its provider event snapshot.
export const fetchMonitorFeedEntry = async (
  monitorId: UUID | '',
  orgId: UUID | '',
  accessToken: string | null | undefined,
  entryId: UUID | '',
): Promise<MonitorFeedEntryDetailOut | null> => {
  if (!monitorId || !orgId || !entryId) return null
  if (!accessToken) {
    throw new Error('No access token')
  }
  return convertDatesInObject(
    await fetchJsonWithInit<MonitorFeedEntryDetailOut>(
      `${monitorsBase(orgId)}/${monitorId}/feed/${entryId}`,
      accessToken,
    ),
    FEED_ENTRY_DATE_FIELDS,
  ) as MonitorFeedEntryDetailOut
}

// Fetch one feed entry's resource snapshot history with the per-step diff.
export const fetchMonitorFeedEntryLineage = async (
  monitorId: UUID | '',
  orgId: UUID | '',
  accessToken: string | null | undefined,
  entryId: UUID | '',
): Promise<MonitorFeedEntryLineageOut | null> => {
  if (!monitorId || !orgId || !entryId) return null
  if (!accessToken) {
    throw new Error('No access token')
  }
  return convertDatesInObject(
    await fetchJsonWithInit<MonitorFeedEntryLineageOut>(
      `${monitorsBase(orgId)}/${monitorId}/feed/${entryId}/lineage`,
      accessToken,
    ),
    FEED_LINEAGE_DATE_FIELDS,
  ) as MonitorFeedEntryLineageOut
}

// Move a feed entry between folders (inbox/archived/trashed).
export const updateMonitorFeedEntryStatus = async (
  monitorId: UUID,
  orgId: UUID | '',
  accessToken: string | null | undefined,
  entryId: UUID,
  status: MonitorFeedEntryStatus,
): Promise<MonitorFeedEntryDetailOut> => {
  if (!orgId) {
    throw new Error('No organization id')
  }
  if (!accessToken) {
    throw new Error('No access token')
  }
  return convertDatesInObject(
    await fetchJsonWithInit<MonitorFeedEntryDetailOut>(
      `${monitorsBase(orgId)}/${monitorId}/feed/${entryId}`,
      accessToken,
      {
        method: 'PATCH',
        headers: JSON_HEADERS,
        body: JSON.stringify({ status }),
      },
    ),
    FEED_ENTRY_DATE_FIELDS,
  ) as MonitorFeedEntryDetailOut
}

// Set the per-user read state of a feed entry.
export const setMonitorFeedEntryRead = async (
  monitorId: UUID,
  orgId: UUID | '',
  accessToken: string | null | undefined,
  entryId: UUID,
  read: boolean,
): Promise<MonitorFeedEntryDetailOut> => {
  if (!orgId) {
    throw new Error('No organization id')
  }
  if (!accessToken) {
    throw new Error('No access token')
  }
  return convertDatesInObject(
    await fetchJsonWithInit<MonitorFeedEntryDetailOut>(
      `${monitorsBase(orgId)}/${monitorId}/feed/${entryId}/read`,
      accessToken,
      {
        method: 'PUT',
        headers: JSON_HEADERS,
        body: JSON.stringify({ read }),
      },
    ),
    FEED_ENTRY_DATE_FIELDS,
  ) as MonitorFeedEntryDetailOut
}

// Move many feed entries between folders in one request. Returns the ids
// actually affected (in the caller's monitor+org scope); foreign/unknown ids
// silently drop out and no 404 is raised. Response is `{count, entry_ids}` with
// no date fields, so no conversion is needed.
export const batchSetMonitorFeedEntryStatus = async (
  monitorId: UUID,
  orgId: UUID | '',
  accessToken: string | null | undefined,
  entryIds: readonly UUID[],
  status: MonitorFeedEntryStatus,
): Promise<MonitorFeedBatchResult> => {
  if (!orgId) {
    throw new Error('No organization id')
  }
  if (!accessToken) {
    throw new Error('No access token')
  }
  return fetchJsonWithInit<MonitorFeedBatchResult>(
    `${monitorsBase(orgId)}/${monitorId}/feed/batch/status`,
    accessToken,
    {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ entry_ids: entryIds, status }),
    },
  )
}

// Set the per-user read state of many feed entries in one request. Idempotent;
// returns the scoped ids now in the requested read state. Response is
// `{count, entry_ids}` with no date fields, so no conversion is needed.
export const batchSetMonitorFeedEntryRead = async (
  monitorId: UUID,
  orgId: UUID | '',
  accessToken: string | null | undefined,
  entryIds: readonly UUID[],
  read: boolean,
): Promise<MonitorFeedBatchResult> => {
  if (!orgId) {
    throw new Error('No organization id')
  }
  if (!accessToken) {
    throw new Error('No access token')
  }
  return fetchJsonWithInit<MonitorFeedBatchResult>(
    `${monitorsBase(orgId)}/${monitorId}/feed/batch/read`,
    accessToken,
    {
      method: 'PUT',
      headers: JSON_HEADERS,
      body: JSON.stringify({ entry_ids: entryIds, read }),
    },
  )
}

// Set the per-user bookmark (star) on a feed entry.
export const setMonitorFeedEntryBookmarked = async (
  monitorId: UUID,
  orgId: UUID | '',
  accessToken: string | null | undefined,
  entryId: UUID,
  bookmarked: boolean,
): Promise<MonitorFeedEntryDetailOut> => {
  if (!orgId) {
    throw new Error('No organization id')
  }
  if (!accessToken) {
    throw new Error('No access token')
  }
  return convertDatesInObject(
    await fetchJsonWithInit<MonitorFeedEntryDetailOut>(
      `${monitorsBase(orgId)}/${monitorId}/feed/${entryId}/bookmark`,
      accessToken,
      {
        method: 'PUT',
        headers: JSON_HEADERS,
        body: JSON.stringify({ bookmarked }),
      },
    ),
    FEED_ENTRY_DATE_FIELDS,
  ) as MonitorFeedEntryDetailOut
}

// ─── Target endpoints (live) ─────────────────────────────────────────────────
// Org-scoped lookups against upstream federal records for the wizard's target
// pickers. Auth/permission semantics are identical to the feed endpoints
// (org READ).

// Typeahead search for monitor targets by recipe target kind. Backend rejects
// queries shorter than 2 trimmed chars (422) — callers gate on that.
type MonitorVendorTargetHitWire = Omit<MonitorVendorTargetHit, 'kind'> & {
  kind: 'entity'
}

type MonitorTargetHitWire =
  | Exclude<MonitorTargetHit, MonitorVendorTargetHit>
  | MonitorVendorTargetHitWire

interface MonitorTargetSearchListWire {
  count: number
  data: MonitorTargetHitWire[]
}

type BackendMonitorTargetSearchKind =
  | Exclude<MonitorTargetSearchKind, 'vendor'>
  | 'entity'

const backendMonitorTargetSearchKind = (
  kind: MonitorTargetSearchKind,
): BackendMonitorTargetSearchKind => (kind === 'vendor' ? 'entity' : kind)

const normalizeMonitorTargetSearchList = (
  kind: MonitorTargetSearchKind,
  response: MonitorTargetSearchListWire,
): MonitorTargetSearchList => {
  if (kind !== 'vendor') {
    return response as MonitorTargetSearchList
  }

  return {
    count: response.count,
    data: response.data.map((hit): MonitorTargetHit => {
      if (hit.kind === 'entity' && hit.entity_type === 'vendor') {
        return { ...hit, kind: 'vendor' }
      }
      return hit as MonitorTargetHit
    }),
  }
}

export const fetchMonitorTargetSearch = async (
  orgId: UUID | '',
  accessToken: string | null | undefined,
  kind: MonitorTargetSearchKind,
  query: string,
  limit: number,
): Promise<MonitorTargetSearchList> => {
  if (!orgId) {
    return { count: 0, data: [] }
  }
  if (!accessToken) {
    throw new Error('No access token')
  }
  const params = new URLSearchParams({
    kind: backendMonitorTargetSearchKind(kind),
    query,
    limit: String(limit),
  })
  const response = await fetchJsonWithInit<MonitorTargetSearchListWire>(
    `${monitorsBase(orgId)}/targets/search?${params.toString()}`,
    accessToken,
  )
  return normalizeMonitorTargetSearchList(kind, response)
}
